<template>
  <div class="login-page" :class="{'dark-mode': darkMode}">
    <!-- Theme Toggle -->
    <div class="theme-toggle">
      <button @click="toggleTheme" class="theme-btn" :aria-label="darkMode ? 'Switch to light mode' : 'Switch to dark mode'">
        <i class="fas" :class="darkMode ? 'fa-sun' : 'fa-moon'"></i>
      </button>
    </div>

    <!-- Animated Background -->
    <div class="animated-bg"></div>
    
    <!-- Main Content -->
    <div class="container main-content">
      <div class="row justify-content-center">
        <div class="col-12 col-md-8 col-lg-6 col-xl-5">
          <!-- Login Card with subtle animations -->
          <transition name="card-slide" appear>
            <div class="card login-card">
              <div class="card-body p-4 p-md-5">
                <!-- Logo with animation -->
                <transition name="fade" appear>
                  <div class="logo-container mb-4">
                    <img src="../assets/logo.png" alt="LMS Logo" class="logo">
                  </div>
                </transition>
                
                <h3 class="text-center mb-4">Welcome Back</h3>
                <p class="text-center text-muted mb-4">Sign in to continue to your account</p>
                
                <!-- Login Form -->
                <form @submit.prevent="laundrylogin" novalidate>
                  <!-- Mobile Input -->
                  <div class="form-floating mb-3">
                    <input 
                      type="tel" 
                      id="mobile" 
                      v-model.trim="login.mobile"
                      class="form-control" 
                      :class="{'is-invalid': errors.mobile}"
                      placeholder="Mobile Number"
                      required
                      @blur="validateMobile"
                      aria-describedby="mobileHelp"
                    >
                    <label for="mobile">Mobile Number</label>
                    <div v-if="errors.mobile" class="invalid-feedback">
                      {{ errors.mobile }}
                    </div>
                    <div id="mobileHelp" class="form-text">We'll never share your number with anyone else.</div>
                  </div>
                  
                  <!-- Password Input -->
                  <div class="form-floating mb-3">
                    <input 
                      :type="showPassword ? 'text' : 'password'" 
                      id="password" 
                      v-model.trim="login.password"
                      class="form-control" 
                      :class="{'is-invalid': errors.password}"
                      placeholder="Password"
                      required
                      maxlength="10"
                      @blur="validatePassword"
                    >
                    <label for="password">Password</label>
                    <button 
                      type="button" 
                      class="btn btn-sm password-toggle"
                      @click="showPassword = !showPassword"
                      :aria-label="showPassword ? 'Hide password' : 'Show password'"
                    >
                      <i class="fas" :class="showPassword ? 'fa-eye-slash' : 'fa-eye'"></i>
                    </button>
                    <div v-if="errors.password" class="invalid-feedback">
                      {{ errors.password }}
                    </div>
                  </div>
                  
                  <!-- Remember me & Forgot password -->
                  <div class="d-flex justify-content-between align-items-center mb-4">
                    <div class="form-check">
                      <input 
                        type="checkbox" 
                        class="form-check-input" 
                        id="rememberMe"
                        v-model="rememberMe"
                      >
                      <label class="form-check-label" for="rememberMe">Remember me</label>
                    </div>
                    <!--<router-link to="/forgot-password" class="text-decoration-none">Forgot password?</router-link>-->
                  </div>
                  
                  <!-- Submit Button -->
                  <button 
                    type="submit" 
                    class="btn btn-primary w-100 py-3 mb-3"
                    :disabled="loading"
                  >
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                    {{ loading ? 'Signing in...' : 'Sign In' }}
                  </button>
                  
                  <!-- Alternative Login Options -->
                  <!--<div class="divider mb-4">-->
                  <!--  <span>Or continue with</span>-->
                  <!--</div>-->
                  
                  <!--<div class="d-grid gap-2 social-login">-->
                  <!--  <button type="button" class="btn btn-outline-secondary">-->
                  <!--    <i class="fab fa-google me-2"></i> Google-->
                  <!--  </button>-->
                  <!--  <button type="button" class="btn btn-outline-secondary">-->
                  <!--    <i class="fab fa-facebook me-2"></i> Facebook-->
                  <!--  </button>-->
                  <!--</div>-->
                </form>
              </div>
              
              <!-- Sign up link -->
              <div class="card-footer bg-transparent text-center py-3">
                <p class="mb-0">Don't have an account? 
                  <router-link :to="'/register'" class="text-decoration-none fw-semibold">Sign up now</router-link>
                </p>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
    
    <!-- Footer -->
    <footer class="footer mt-auto">
      <div class="container">
        <div class="d-flex align-items-center justify-content-center py-3">
          <p class="mb-0 small">&copy; {{ new Date().getFullYear() }} LMS Platform. All rights reserved.</p>
        </div>

        <div class="terms">
        
            <router-link to="/term" class="text-decoration-none fw-semibold">
                Terms and Condition
              </router-link>
                <router-link to="/privacy" class="text-decoration-none fw-semibold">
                Privacy Policy
              </router-link>
                <router-link to="/Refund" class="text-decoration-none fw-semibold">
                Refund Policy
              </router-link>
                <router-link to="/shipping" class="text-decoration-none fw-semibold">
                Shipping Policy
              </router-link>


          </div>


      </div>
    </footer>
  </div>
</template>

<style scoped>


.terms {
  display: flex;
  justify-content: center; /* center horizontally */
  gap: 1.5rem; /* spacing between links */
  flex-wrap: wrap; /* wrap on smaller screens */

}



.login-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding-top: 2rem;
}

.dark-mode {
  background: linear-gradient(135deg, #232526 0%, #414345 100%);
  color: #f8f9fa;
}

/* Animated Background */
.animated-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 40vh;
  background: linear-gradient(57deg, #6027be, #000327);
  border-radius: 0 0 50% 50%/ 0 0 100% 100%;
  transform: scaleX(1.5);
  z-index: 0;
  animation: gradientShift 15s ease infinite;
}

.dark-mode .animated-bg {
  background: linear-gradient(57deg, #2d1b69, #0a0826);
}

@keyframes gradientShift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

/* Theme Toggle */
.theme-toggle {
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 10;
}

.theme-btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  transition: all 0.3s ease;
}

.dark-mode .theme-btn {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.theme-btn:hover {
  transform: rotate(15deg);
  background: rgba(255, 255, 255, 0.3);
}

/* Main Content */
.main-content {
  position: relative;
  z-index: 1;
  padding-top: 2rem;
  padding-bottom: 2rem;
}

/* Login Card */
.login-card {
  border: none;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.85);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.dark-mode .login-card {
  background: rgba(30, 30, 40, 0.85);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  color: #f8f9fa;
}

.login-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.15);
}

.dark-mode .login-card:hover {
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.4);
}

/* Logo */
.logo-container {
  text-align: center;
}

.logo {
  max-width: 180px;
  height: auto;
  transition: all 0.5s ease;
}

.logo:hover {
  transform: scale(1.05);
}

/* Form Elements */
.form-floating {
  position: relative;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: #6c757d;
  z-index: 5;
}

.dark-mode .form-control {
  background-color: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.15);
  color: #f8f9fa;
}

.dark-mode .form-control:focus {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: var(--primary);
  color: #f8f9fa;
  box-shadow: 0 0 0 0.25rem rgba(99, 102, 241, 0.25);
}

.dark-mode .form-control::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.dark-mode .form-check-input {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.3);
}

.dark-mode .form-text {
  color: rgba(255, 255, 255, 0.6) !important;
}

/* Divider */
.divider {
  display: flex;
  align-items: center;
  text-align: center;
  color: #6c757d;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid #dee2e6;
}

.divider::before {
  margin-right: 0.5em;
}

.divider::after {
  margin-left: 0.5em;
}

.dark-mode .divider {
  color: rgba(255, 255, 255, 0.6);
}

.dark-mode .divider::before,
.dark-mode .divider::after {
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

/* Footer */
.footer {
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.dark-mode .footer {
  background: rgba(0, 0, 0, 0.2);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
}

/* Animations */
.card-slide-enter-active {
  animation: slideInUp 0.6s ease;
}

.fade-enter-active {
  animation: fadeIn 0.8s ease 0.3s both;
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .login-page {
    padding-top: 1rem;
  }
  
  .animated-bg {
    height: 30vh;
  }
  
  .theme-toggle {
    top: 1rem;
    right: 1rem;
  }
  
  .theme-btn {
    width: 40px;
    height: 40px;
  }
}
</style>

<script>
import axios from "@/plugins/axios";
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';

export default {
  name: 'LoginView',
  setup() {
    const router = useRouter();
    const darkMode = ref(false);
    const loading = ref(false);
    const showPassword = ref(false);
    const rememberMe = ref(false);
    const login = ref({
      mobile: '',
      password: ''
    });
    const errors = ref({
      mobile: '',
      password: ''
    });

    // Check for saved theme preference or respect OS preference
    onMounted(() => {
      document.getElementById('global-loader').style.display = 'none';
      const token = localStorage?.getItem("token");
      if (token) {
        router.replace('/dashboard');
      }
      
      // Check for saved theme preference
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) {
        darkMode.value = savedTheme === 'dark';
      } else {
        // Respect OS preference if no saved preference
        darkMode.value = window.matchMedia('(prefers-color-scheme: dark)').matches;
      }
      applyTheme();
      
      // Check for remembered credentials
      const rememberedMobile = localStorage.getItem('rememberedMobile');
      if (rememberedMobile) {
        login.value.mobile = rememberedMobile;
        rememberMe.value = true;
      }
    });

    const toggleTheme = () => {
      darkMode.value = !darkMode.value;
      localStorage.setItem('theme', darkMode.value ? 'dark' : 'light');
      applyTheme();
    };

    const applyTheme = () => {
      if (darkMode.value) {
        document.documentElement.setAttribute('data-bs-theme', 'dark');
      } else {
        document.documentElement.setAttribute('data-bs-theme', 'light');
      }
    };

    const validateMobile = () => {
      if (!login.value.mobile) {
        errors.value.mobile = 'Mobile number is required';
        return false;
      }
      
      const mobileRegex = /^[0-9]{10}$/;
      if (!mobileRegex.test(login.value.mobile)) {
        errors.value.mobile = 'Please enter a valid 10-digit mobile number';
        return false;
      }
      
      errors.value.mobile = '';
      return true;
    };

    const validatePassword = () => {
      if (!login.value.password) {
        errors.value.password = 'Password is required';
        return false;
      }
      
    //   if (login.value.password.length < 6) {
    //     errors.value.password = 'Password must be at least 6 characters';
    //     return false;
    //   }
      
      errors.value.password = '';
      return true;
    };

    const laundrylogin = async () => {
      // Validate form
      const isMobileValid = validateMobile();
      const isPasswordValid = validatePassword();
      
      if (!isMobileValid || !isPasswordValid) return;
      
      loading.value = true;
      
      try {
        const response = await axios.post("student_login", login.value, {
          headers: { "Content-Type": "application/json" }
        });
        
        if (response.data.success == 1) {
          localStorage.setItem('token', response.data.token);
          
          // Remember mobile if requested
          if (rememberMe.value) {
            localStorage.setItem('rememberedMobile', login.value.mobile);
          } else {
            localStorage.removeItem('rememberedMobile');
          }
          
          // Show success message
        //   showNotification('Success!', 'Login successful', 'success');
          
          
            router.replace('/dashboard');
          
        } else if (response.data.success == 2) {
          showNotification(
            'Account Inactive', 
            response.data.message, 
            'warning',
            response.data.activate
          );
        } else {
          showNotification('Error', response.data.message, 'error');
        }
      } catch (err) {
        showNotification('Error', 'Login failed. Please try again.', 'error');
        console.error('Login error:', err);
      } finally {
        loading.value = false;
      }
    };

    const showNotification = (title, message, type, activate = null) => {
      // In a real implementation, you would use a notification library
      // For now, we'll use a simple alert
      if (type === 'warning' && activate) {
        if (confirm(`${title}\n${message}\n\nWould you like to activate your account?`)) {
          window.open(`https://wa.me/${activate}?text=${encodeURIComponent("Reactivate my laundry account")}`, '_blank');
        }
      } else {
        alert(`${title}\n${message}`);
      }
    };

    return {
      darkMode,
      loading,
      showPassword,
      rememberMe,
      login,
      errors,
      toggleTheme,
      validateMobile,
      validatePassword,
      laundrylogin
    };
  }
}
</script>